import React, { useState, useEffect, useRef }  from 'react';
import { AskConfirm, ConfirmPopup } from 'components/ConfirmPopup.jsx';
import { ContentPopup } from 'components/ContentPopup.jsx';
import { GetRequest, PostRequest } from 'components/common';
import QuoteBuilderForm from 'components/quote_builder/QuoteBuilderForm.jsx';
import LibraryAttributePicker from 'components/quote_builder/LibraryAttributePicker.jsx';
import AttributePropertyTags from 'components/quote_builder/attributePropertyTags.jsx';

const SettingsUserUnitPriceAttributeList = ( { userUnitPriceKey, tabConfig, sectionAttributes, showNewAttributeForm, editAttributeId, urlTabKey } ) => {
  const [inProgress, setInProgress] = useState(true)
  const confirmPopupRef = useRef({});
  const [attributes, setAttributes] = useState([])
  const [mode, setMode] = useState(showNewAttributeForm ? 'new' : (editAttributeId ? 'editing' : 'listing'));
  const [config, setConfig] = useState({});
  const [editingId, setEditingId] = useState(editAttributeId || '');
  const [newAttributeFormError, setNewAttributeFormError] = useState('');
  const [newAttributeValue, setNewAttributeValue] = useState('');
  const [showLibraryPopup, setShowLibraryPopup] = useState(false);
  const libraryPopupRef = useRef();
  // Opt-in per attribute tab (settings.yml `library_enabled`). When on, "Yeni" opens the library
  // picker; when off the button keeps its original behaviour (straight to the blank form).
  const libraryEnabled = tabConfig.library_enabled === true;

  // The :tab segment of the settings URL. A by-unit tab passes the PARENT tab key (which the tab
  // resolver and editAttributeIdForTab match on); data operations below stay on this sub-attribute's
  // own tabConfig.key. Plain tabs pass nothing, so the two coincide.
  const settingsTabKey = urlTabKey || tabConfig.key;
  const newUrl = `/quote-builder/settings/${userUnitPriceKey}/${settingsTabKey}/new`;
  const listingUrl = `/quote-builder/settings/${userUnitPriceKey}/${settingsTabKey}`;
  const editUrlFor = (id) => `/quote-builder/settings/${userUnitPriceKey}/${settingsTabKey}/${id}/edit`;

  const switchToNewMode = () => {
    setMode('new');
    window.history.pushState({}, '', newUrl);
  };

  // The "Yeni" button. With the library enabled it opens the picker; otherwise it goes straight to the
  // blank form exactly as before.
  const startNewMachine = () => {
    if (libraryEnabled) {
      setShowLibraryPopup(true);
    } else {
      switchToNewMode();
    }
  };

  // "Enter manually" inside the picker: close it and fall through to the existing blank form.
  const handleManualEntry = () => {
    setShowLibraryPopup(false);
    switchToNewMode();
  };

  // A library machine was added to the user's account. Refresh the menu/tab counts; the popup stays open
  // so the user can add several in a row (no jump to the edit form).
  const handleLibraryCopy = () => {
    document.dispatchEvent(new CustomEvent('quote_builder.user_unit_price_saved', {}));
  };

  // Closing the popup (Done / × / Esc): return to the listing and refetch it so the machines just added
  // appear.
  const closeLibraryPopup = () => {
    setShowLibraryPopup(false);
    fetchIAttributes();
  };

  // The single exit back to the listing, from the new form or an attribute edit. Restores the
  // listing URL from whichever deep-linked URL we are on (.../new or .../:id/edit) and clears the
  // editing id so a stale id can't reopen the edit form.
  const switchToListingMode = () => {
    setMode('listing');
    setEditingId('');
    if (window.location.pathname !== listingUrl) {
      window.history.pushState({}, '', listingUrl);
    }
  };

  const fetchIAttributes = () => {
    // https://diecuttemplates.com.dev/quote-builder/user_unit_price_attributes/list/corrugated_cardboard_layer
    if(mode != 'listing') { return }
    setInProgress( () => true );
    GetRequest({
      url: `/quote-builder/user_unit_price_attributes/list/${tabConfig.key}`,
      callback: (data) => {
        setAttributes( () => data);
        setInProgress( () => false );
      }
    })
  }

  const swithToEditMode = (e, id) => {
    if(e != undefined) { e.preventDefault(); }
    setEditingId( () => id )
    setMode( () => 'editing');
    // Deep-link this edit so a refresh reopens the same attribute (id + key + "edit" in the URL).
    window.history.pushState({}, '', editUrlFor(id));
  }

  const swithToListingMode = (e) => {
    if(e != undefined) { e.preventDefault(); }
    setMode(() => 'listing');
    setEditingId( () => '' )
  }

  const deleteConfirm = (e, id) => {
    if(e != undefined) { e.preventDefault(); }
    AskConfirm(
      confirmPopupRef,
      t('quote_builder.delete_confirm'),
      performDelete,
      id,
      { confirmLabel: t('quote_builder.delete'), danger: true }
    );
  }

  const performDelete = (id) => {
    setInProgress( () => true );
    PostRequest({
      url: `/quote-builder/user_unit_price_attributes/delete/${tabConfig.key}/${id}`,
      callback: () => {
        fetchIAttributes();
        // Refresh the menu/tab counts (the rich sub-form paths already dispatch via QuoteBuilderForm).
        document.dispatchEvent(new CustomEvent('quote_builder.user_unit_price_deleted', {}));
      },
    })
  }

  // The record's summary pills. Shared with the library picker (attributePropertyTags.jsx) so the same
  // record reads identically in the settings list and in the "copy from library" popup.
  const attributeProperties = (attribute) => (
    <AttributePropertyTags
      properties={attribute.properties}
      listingProperties={tabConfig.listing_properties}
      subFormAttributes={config?.create_sub_form?.attributes}
      sectionAttributes={sectionAttributes}
    />
  );

  const attributeRow = (attribute) => {
    return <tr key={"" + attribute.id + mode }>
      <td> <strong>{attribute.name}</strong> {attributeProperties(attribute)} </td>
      <td>
        <div className="float-right" style={{ whiteSpace: 'nowrap' }}>
          {attribute.can_be_edited && <a href="#" onClick={(e) => { swithToEditMode(e, attribute.id) } }>
            <i className="fa fa-pencil-square-o" aria-hidden="true"></i> {t('quote_builder.edit')}
          </a>}

          {!attribute.can_be_edited && <a className="disabled">
            <i className="fa fa-pencil-square-o" aria-hidden="true"></i> {t('quote_builder.edit')}
          </a>}

          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;

          {attribute.can_be_deleted && <a href="#" onClick={(e) => { deleteConfirm(e, attribute.id) } }>
            <i className="fa fa-trash-o" aria-hidden="true"></i> {t('quote_builder.delete')}
          </a>}

          {!attribute.can_be_deleted && <a className="disabled">
            <i className="fa fa-trash-o" aria-hidden="true"></i> {t(`quote_builder.cannot_delete_reason_${attribute.cant_be_deleted_reason}`)}
          </a>}
        </div>
      </td>
    </tr>
  }


  const getConfig = () => {
    // https://diecuttemplates.com.dev/quote-builder/user_unit_price_attributes/config/corrugated_cardboard_material/corrugated_cardboard_layer
    setInProgress( () => true );
    GetRequest({
      url: `/quote-builder/user_unit_price_attributes/config/${userUnitPriceKey}/${tabConfig.key}`,
      callback: (data) => {
        setConfig( () => data );
        setInProgress( () => false );
      },
    })
  }

  const handleAttributeValueClick = () => {
    const postUrl = (parseInt(editingId) > 0) ? `/quote-builder/user_unit_price_attributes/update_name/${editingId}` : `/quote-builder/user_unit_price_attributes/create/${config.key}`
    setInProgress( () => true );
    PostRequest({
      url: postUrl,
      data: {
        name: newAttributeValue,
      },
      callback: (_data) => {
        switchToListingMode();
        setInProgress( () => false );
        // Refresh the menu/tab counts after creating/renaming an attribute.
        document.dispatchEvent(new CustomEvent('quote_builder.user_unit_price_saved', {}));
      }
    })
  }

  const newAttributeForm = () => {
    return (
      <>
      <br />
      <div className="form-inline newAttributeForm" style={{ marginTop: '10px', marginLeft: '10px' }}>
        <div className="form-group">
          <div className="input-group">
            <div className="input-group-addon">
              {config.create_input_label != undefined && t(config.create_input_label)}
              {config.create_input_label == undefined && t('quote_builder.new_attribute_with_key').replace('%{key}', t(`quote_builder.${config.key}`).toLowerCase())}
              </div>
              <input
                type="text"
                className={newAttributeFormError != '' ? 'input-error form-control' : 'form-control'}
                size="25"
                value={newAttributeValue}
                onChange={(e) => {setNewAttributeValue(e.target.value)}}
                />
            </div>
          </div>

          <button type="button" className="btn simple-btn" onClick={handleAttributeValueClick} > {t('quote_builder.save')} </button> &nbsp;
          <button className="btn" type="button" onClick={switchToListingMode}> {t('quote_builder.cancel')} </button>

          {newAttributeFormError != '' && <center> <small className="error"> {t(newAttributeFormError)} </small> </center> }
      </div>
      </>
    )
  }

  // A top-right "X" that cancels the sub-form and returns to the listing (a quick alternative to
  // scrolling down to Cancel).
  const subFormCloseButton = () => (
    <button
      type="button"
      className="attribute-subform__close"
      onClick={switchToListingMode}
      aria-label={t('quote_builder.cancel')}
      title={t('quote_builder.cancel')}
    >
      &times;
    </button>
  );

  const newAttributeSubForm = () => {
    const afterSave = () => { switchToListingMode() }
    return (<div className="attribute-subform">
      {subFormCloseButton()}
      <QuoteBuilderForm config={config.create_sub_form} afterSave={afterSave} afterCancel={afterSave} />
    </div>)
  }

  const editAttributeForm = () => {
    return newAttributeForm();
  }

  const editAttributeSubForm = () => {
    const afterSave = () => { switchToListingMode() }

    return (<div className="attribute-subform">
      {subFormCloseButton()}
      <QuoteBuilderForm config={config.create_sub_form} afterSave={afterSave} afterCancel={afterSave}
        showingState="edit" editStateId={editingId} editedKlass="UserUnitPriceAttribute" linkToSettings={false}
      />
    </div>)
  }

  useEffect(() => {
    if (!showNewAttributeForm && mode === 'new') {
      setMode('listing');
    }
  }, [showNewAttributeForm])

  // Mirror of the new-form reset: when the deep-linked edit id clears (browser Back to the listing
  // URL in the same section, where the component does not remount), leave edit mode. Keyed on the
  // prop only, so opening edit via the pencil (which changes internal state, not the prop) is unaffected.
  useEffect(() => {
    if (!editAttributeId && mode === 'editing') {
      setMode('listing');
      setEditingId('');
    }
  }, [editAttributeId])

  useEffect(fetchIAttributes, [mode])
  useEffect(() => {
    if(mode == 'listing') {
      setNewAttributeValue( () => '')
    }

    if(mode == "editing" && config.create_sub_form == undefined) {
      setInProgress( () => true );
      GetRequest({
        url: `/quote-builder/user_unit_price_attribute/${editingId}`,
        callback: (data) => {
          setNewAttributeValue( () => data.name );
          setInProgress( () => false );
        },
      })
    }
  }, [mode])

  useEffect(getConfig, [])

  if(inProgress && attributes.length == 0) { return t('quote_builder.please_wait') }

  return (<>
    {mode == "listing" && <table className={`table table-striped table-bordered-outside ${inProgress ? 'tableInProgress' : ''} `} style={{ marginTop: "20px"}}>
      <tbody>
        <tr>
          <th colSpan="2">
            <div className="float-left">
              { t(tabConfig.label) } ({attributes.length})
            </div>
            {tabConfig.create_enabled !== false &&
              <div className="float-right">
                <button onClick={startNewMachine} className="dct-button dct-button--primary dct-button--small">
                  <i className="fa fa-plus" aria-hidden="true"></i> {t('nav.new')}
                </button>
              </div>
            }
            <div style={{ clear: 'both' }}></div>
          </th>
        </tr>

        {attributes.map(attributeRow)}

        {tabConfig.create_enabled !== false && <tr>
          <td colSpan="2" style={{ paddingTop: '15px', paddingBottom: '15px' }}>
            <div className="float-left">
              <button onClick={startNewMachine} className="btn simple-btn">
                <i className="fa fa-plus-square-o" aria-hidden="true"></i> &nbsp;
                  { tabConfig.new_title!= undefined && t(tabConfig.new_title) }
                  { tabConfig.new_title == undefined && t('nav.new') }
              </button>
            </div>
          </td>
        </tr>}

      </tbody>
    </table>}

    {mode == "editing" && config.create_sub_form == undefined && editAttributeForm() }
    {mode == "editing" && config.create_sub_form != undefined && editAttributeSubForm() }
    {mode == "new" && config.create_sub_form == undefined && newAttributeForm() }
    {mode == "new" && config.create_sub_form != undefined && newAttributeSubForm() }

    {showLibraryPopup && <ContentPopup
      myRef={libraryPopupRef}
      autoOpen={true}
      notWide={true}
      // The shared .modal-content sets padding-top: 0; add it back here (matching the 20px sides) so the
      // picker title is not flush against the top edge. Scoped to this modal via inline style.
      modalStyle={{ maxWidth: '640px', paddingTop: '20px' }}
      cancelButton={false}
      beforeClose={closeLibraryPopup}
      content={<LibraryAttributePicker
        userUnitPriceKey={userUnitPriceKey}
        tabConfig={tabConfig}
        onCopy={handleLibraryCopy}
        onManualEntry={handleManualEntry}
        onClose={closeLibraryPopup}
      />}
    />}

    <ConfirmPopup myRef={confirmPopupRef} />
  </>
  )
}

export default SettingsUserUnitPriceAttributeList;
